import { getPercentageStringFromDecimal } from '@Utils/helpers/numericalHelpers';
import { Autocomplete, Dialog, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, isDate, isValid, parseISO } from 'date-fns';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import NumberInput from '@Components/DomElements/NumberInput';
import TextInput from '@Components/DomElements/TextInput';
import {
    ModalBody,
    ModalFooter,
    ModalHeader,
} from '@Components/ModalComponents';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import { visibleFoldersState } from '@Data/folders/Reducer';
import {
    DEFAULT_TARGET_INSPECTION_RATE,
    DEFAULT_TARGET_YIELD_RATE,
} from '@Data/qualityControl/Interfaces';
import TeamsApi from '@Data/teams/Api';
import { teamMembersState } from '@Data/teams/Reducer';
import Styles from './JobSettingsDialog.scss';

interface IJobSettingsDialogProps {
    isOpen: boolean;
    selectedFolder: IFolder;
    selectedFolders: Array<string>;
    onCancel: () => void;
    onComplete: () => void;
}

const convertPercentToDecimal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percentage = parseInt(e.target.value);
    return percentage / 100;
};

const JobSettingsDialog = (props: IJobSettingsDialogProps) => {
    const { isOpen, selectedFolder, onCancel, onComplete } = props;
    const teamMembers = useSelector(teamMembersState);
    const { projectId, teamId } = useParams();
    const [alterFields, setAlteredFields] = useState<Array<string>>([]);
    const teamMembersEmails = teamMembers.map((m) => m.email);
    const [dueDate, setDueDate] = useState<Date>(null);
    const [targetDate, setTargetDate] = useState<Date>(null);

    const targetDateSetter = (
        <DatePicker
            label="Target Date"
            value={targetDate}
            sx={{ width: '100%' }}
            referenceDate={addDays(new Date(), 11)}
            onChange={(newValue) => {
                if (!alterFields.includes('target_date')) {
                    setAlteredFields([...alterFields, 'target_date']);
                }
                setTargetDate(newValue);
            }}
        />
    );

    const dueDateSetter = (
        <DatePicker
            label="Due Date"
            value={dueDate}
            referenceDate={addDays(new Date(), 14)}
            sx={{ width: '100%' }}
            onChange={(newValue) => {
                if (!alterFields.includes('due_date')) {
                    setAlteredFields([...alterFields, 'due_date']);
                }
                setDueDate(newValue);
            }}
        />
    );

    useEffect(() => {
        if (isOpen) {
            setAlteredFields([]);
            const initialInspectionRate = !_isNil(
                selectedFolder?.target_inspection_rate
            )
                ? selectedFolder.target_inspection_rate
                : DEFAULT_TARGET_INSPECTION_RATE;

            setInspectionRate(initialInspectionRate);

            const initialYieldRate = selectedFolder?.target_pass_yield
                ? selectedFolder?.target_pass_yield
                : DEFAULT_TARGET_YIELD_RATE;

            setYieldRate(initialYieldRate);

            let targetDate: Date = extractDateOrNull(
                selectedFolder?.target_date
            );
            let dueDate: Date = extractDateOrNull(selectedFolder?.due_date);
            setTargetDate(targetDate);
            setDueDate(dueDate);
            setSowName(selectedFolder?.sow_name);
            setAssignedQcAnalyst(selectedFolder?.assigned_qc_analyst);
            setNote(selectedFolder?.note);

            function extractDateOrNull(date: string) {
                let dateObject = null;
                if (date == null || date == '') {
                    console.log('date is null or empty');
                } else {
                    dateObject = parseISO(date);
                }
                return dateObject;
            }
        }
    }, [selectedFolder, isOpen]);

    useEffect(() => {
        if (props.isOpen && (_isNil(teamMembers) || teamMembers.length === 0)) {
            TeamsApi.getTeamMembers(teamId);
        }
    }, [props.isOpen, teamMembers, teamId]);

    const visibleFolders = useSelector(visibleFoldersState);
    const [otherSelectedFolders, setOtherSelectedFolders] = useState<
        Array<IFolder>
    >([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [inspectionRate, setInspectionRate] = useState<number>(
        DEFAULT_TARGET_INSPECTION_RATE
    );
    const [yieldRate, setYieldRate] = useState<number>(
        DEFAULT_TARGET_YIELD_RATE
    );
    useEffect(() => {
        if (isOpen) {
            setOtherSelectedFolders(
                visibleFolders.filter(
                    (f) =>
                        _isNil(selectedFolder) ||
                        f.folder_id != selectedFolder.folder_id
                )
            );
        }
    }, [visibleFolders, selectedFolder, isOpen]);

    const [sowName, setSowName] = useState<string>(null);
    const [note, setNote] = useState<string>(null);

    const [assignedQcAnalyst, setAssignedQcAnalyst] = useState<string>(null);

    const handleInspectionRateChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!alterFields.includes('target_inspection_rate')) {
            setAlteredFields([...alterFields, 'target_inspection_rate']);
        }
        setInspectionRate(convertPercentToDecimal(e));
    };

    const handleYieldRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!alterFields.includes('target_pass_yield')) {
            setAlteredFields([...alterFields, 'target_pass_yield']);
        }
        setYieldRate(convertPercentToDecimal(e));
    };

    const handleSubmit = async () => {
        const updateFolderDetails = {
            target_inspection_rate: inspectionRate,
            target_pass_yield: yieldRate,
            due_date: dueDate,
            target_date: targetDate,
            sow_name: sowName,
            assigned_qc_analyst: assignedQcAnalyst,
            note: note,
        };
        let alteredObject = {};
        alterFields.forEach((field) => {
            alteredObject[field] = updateFolderDetails[field];
        });
        setIsSubmitting(true);
        let actionFolders = [selectedFolder, ...otherSelectedFolders];
        actionFolders.map(async (folder, idx) => {
            await FoldersApi.editFolderDetails(alteredObject, folder, () => {
                if (actionFolders.length === idx + 1) {
                    onComplete();
                    setIsSubmitting(false);
                    setAlteredFields([]);
                }
            });
        });
    };

    const resetInputs = () => {
        setInspectionRate(DEFAULT_TARGET_INSPECTION_RATE);
        setYieldRate(DEFAULT_TARGET_YIELD_RATE);
        setAlteredFields([]);
    };

    const handleCancel = () => {
        resetInputs();
        onCancel();
    };

    const inspectionRateSetter = (
        <NumberInput
            value={getPercentageStringFromDecimal(inspectionRate)}
            onChange={handleInspectionRateChange}
            min={1}
            max={100}
            step={1}
            containerStyle={{
                width: '100%',
            }}
            label="Inspection Rate %"
        />
    );

    const yieldRateSetter = (
        <NumberInput
            value={getPercentageStringFromDecimal(yieldRate)}
            onChange={handleYieldRateChange}
            min={1}
            max={100}
            label="Yield Rate %"
            step={1}
            containerStyle={{
                width: '100%',
            }}
        />
    );

    const assignedQcAnalystSetter = (
        <Autocomplete
            options={teamMembersEmails}
            value={assignedQcAnalyst}
            placeholder="Assign To QC Analyst"
            onChange={(e, v) => {
                if (!alterFields.includes('assigned_qc_analyst')) {
                    setAlteredFields([...alterFields, 'assigned_qc_analyst']);
                }
                setAssignedQcAnalyst(v);
            }}
            isOptionEqualToValue={(option: string, value: string) => {
                return option === value;
            }}
            sx={{
                width: '100%',
            }}
            renderInput={(params) => (
                <TextField
                    sx={{
                        width: 'inherit',
                    }}
                    {...params}
                    label="Qc Analyst"
                />
            )}
        />
    );

    const sowNameSetter = (
        <TextField
            label="SoW"
            value={sowName}
            sx={{
                width: '100%',
            }}
            onChange={(newValue) => {
                if (!alterFields.includes('sow_name')) {
                    setAlteredFields([...alterFields, 'sow_name']);
                }
                setSowName(newValue.target.value);
            }}
        />
    );
    const noteSetter = (
        <TextField
            label="Note"
            value={note}
            multiline={true}
            rows={9}
            sx={{ width: '100%' }}
            onChange={(newValue) => {
                if (!alterFields.includes('note')) {
                    setAlteredFields([...alterFields, 'note']);
                }
                setNote(newValue.target.value);
            }}
        />
    );

    const canSubmit =
        inspectionRate > 0 &&
        inspectionRate < 1 &&
        yieldRate > 0 &&
        yieldRate < 1;

    return (
        <Dialog
            id="job-settings-dialog"
            open={isOpen}
            maxWidth="md"
            sx={{
                ['& .MuiDialog-container']: {
                    alignItems: 'flex-start',
                },
                ['.MuiPaper-root']: {
                    width: '50vw',
                },
            }}
        >
            <ModalHeader
                id="job-settings-dialog-header"
                title={`Job Settings - ${
                    selectedFolder?.folder_name
                } ${otherSelectedFolders.map((f) => f.folder_name).join(' ')}`}
            />
            <ModalBody>
                <div className={Styles.jobSettingsContainer}>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>
                            Target Inspection Rate:
                        </div>
                        <div className={Styles.jobSettingsValue}>
                            {inspectionRateSetter}
                        </div>
                    </div>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>
                            Target Yield Rate:
                        </div>
                        <div className={Styles.jobSettingsValue}>
                            {yieldRateSetter}
                        </div>
                    </div>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>
                            Target Date:
                        </div>
                        <div className={Styles.jobSettingsValue}>
                            {targetDateSetter}
                        </div>
                    </div>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>Due Date:</div>
                        <div className={Styles.jobSettingsValue}>
                            {dueDateSetter}
                        </div>
                    </div>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>QC Analyst:</div>
                        <div className={Styles.jobSettingsValue}>
                            {assignedQcAnalystSetter}
                        </div>
                    </div>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>SoW:</div>
                        <div className={Styles.jobSettingsValue}>
                            {sowNameSetter}
                        </div>
                    </div>
                    <div className={Styles.jobSettingsRow}>
                        <div className={Styles.jobSettingsKey}>Note:</div>
                        <div className={Styles.jobSettingsValue}>
                            {noteSetter}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                submitText="Save"
                canSubmit={canSubmit}
                isSubmitting={isSubmitting}
            />
        </Dialog>
    );
};

export default JobSettingsDialog;
